/**
 * Copyright © 2025 Farmlands Co-operative Society Limited. All rights reserved.
 *
 * This document contains confidential information and is the property of Farmlands.
 * Unauthorised reproduction or distribution of any part of this publication is prohibited without written consent.
 */

import { FC, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Alert, Stack } from '@mui/material';
import TestIds from '../../../testIds';
import { useSelector } from 'react-redux';
import { selectCurrentFarmerAccount } from '../../azure-b2c/storage';
import { PageContainer } from '../../../components/layout/PageContainer';
import TitleBox from '../../../components/TitleBox';
import { useActivities, useActivitiesLookups } from '../api/hooks/useActivities';
import { ActivitiesResponse, Activity, LookupsResponse } from '../api/types/types';
import ActivitiesList from '../components/ActivitiesList/ActivitiesList';
import CreateActivityModal from '../components/CreateActivityModal/CreateActivityModal';
import ActivitiesHeader from '../components/ActivitiesHeader.tsx/ActivitiesHeader';
import ContactDetailDisplay from '../components/ContactDetailDisplay/ContactDetailDisplay';
import { useThemeBackgroundColor } from '../../../hooks/useThemeBackgroundColor';
import { ACTIVITIES_ROUTE } from '../activities-routes';

const Activities: FC = (): JSX.Element => {
  const account = useSelector(selectCurrentFarmerAccount);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const navigate = useNavigate();

  const {
    data: activitiesData,
    isLoading: activitiesLoading,
    error: activitiesError,
  } = useActivities(account?.accountNumber ?? '');
  const {
    data: lookupsData,
    isLoading: lookupsLoading,
    error: lookupsError,
  } = useActivitiesLookups();
  useThemeBackgroundColor((theme) => theme.colors.shade0);

  const campaigns = (activitiesData as ActivitiesResponse)?.campaigns || [];
  const lookups = (lookupsData as LookupsResponse)?.lookups || [];

  if (activitiesError || lookupsError) {
    return (
      <PageContainer data-testid={TestIds.pages.activities}>
        <TitleBox display='Activities' />
        <Alert severity='error' data-testid={TestIds.feat.activities.error}>
          Failed to load activities. Please try again later.
        </Alert>
      </PageContainer>
    );
  }

  return (
    <PageContainer data-testid={TestIds.pages.activities}>
      <ActivitiesHeader onCreateActivity={() => setIsCreateModalOpen(true)} />

      <Stack
        gap={{ xs: 3, lg: 2 }}
        direction={{ xs: 'column', lg: 'row' }}
        alignItems={{ xs: 'stretch', lg: 'start' }}
      >
        <ActivitiesList
          loading={activitiesLoading || lookupsLoading}
          activities={(activitiesData as ActivitiesResponse)?.activities || []}
          lookups={lookups}
          onActivityClick={(activity: Activity) =>
            navigate(`${ACTIVITIES_ROUTE}/${activity.activityId}`)
          }
        />
        <ContactDetailDisplay />
      </Stack>

      {isCreateModalOpen && (
        <CreateActivityModal
          open={isCreateModalOpen}
          onClose={() => setIsCreateModalOpen(false)}
          contactPersons={(activitiesData as ActivitiesResponse)?.contactPersons || []}
          campaigns={campaigns}
          lookups={lookups}
        />
      )}
    </PageContainer>
  );
};

export default Activities;
